import * as t from 'io-ts';
import { parse } from '@dha/io-ts-parse';

const ContaminantTS = t.type({
    description: t.string,
    AHPreferredCode: t.string,
    AEPContaminationGroupCode: t.string,
    isGroundWaterImpacted: t.string,
    isSurfaceWaterImpacted: t.string,
    isOnSiteSoilImpacted: t.string,
    isOffSiteSoilImpacted: t.string
});
export type ContaminantIO = t.TypeOf<typeof ContaminantTS>;

const CoordinatesTS = t.type({
    latitude: t.number,
    longitude: t.number,
});
export type CoordinatesIO = t.TypeOf<typeof CoordinatesTS>;

const LegalLandDescriptionTS = t.type({
    MER: t.string,
    RGE: t.string,
    TWP: t.string,
    SEC: t.string,
    QTR: t.string,
    LSD: t.string,
    Plan: t.string,
    Block: t.string,
    Lot: t.string,
});
export type LegalLandDescriptionIO = t.TypeOf<typeof LegalLandDescriptionTS>;

const RiskManagementDataTS = t.type({
    RMPLocation: t.string,
    approvedRMPInPlace: t.string,
    DUA: t.string,
    FAL: t.string,
    managementLimit: t.string,
    ecologicalDirectSoilContact: t.string,
    humanDirectSoilContact: t.string,
    vapourInhalation: t.string,
    others: t.string,
    administrativeControl: t.string,
    exposureBarriers: t.string,
});
export type RiskManagementDataIO = t.TypeOf<typeof RiskManagementDataTS>;

const SiteTS = t.intersection([
    t.type({
        id: t.string,
        operationName: t.string,
        documentsInFile: t.number,
        coordinates: t.array(CoordinatesTS),
        contaminants: t.array(ContaminantTS),
        LLD: t.array(LegalLandDescriptionTS),
        riskManagementData: t.array(RiskManagementDataTS)
    }),
    t.partial({
        operationId: t.union([t.number, t.undefined]),
        numOfRecords: t.union([t.number, t.undefined]),
        UdateFileOpened: t.string,
        lastAEPReply: t.string,
        lastItemInFileDate: t.string,
        operatorName: t.string,
        municipality: t.string,
        city: t.string,
        neighborhood: t.string,
        yearOfESAReport: t.string,
        environmentalConsultingCompany: t.string,
        specificActivityCode: t.string,
        oprAreaParcelId: t.union([t.number, t.undefined]),
        isThereRecordOfSiteCondition: t.string,
        statusOfSiteConditionRecord: t.string,
        currentSiteStatus: t.string,
        rationaleForSiteStatus: t.string,
        siteCriteriaSelection: t.string,
        // yearOfCriteriaSelection: t.string,
        criteriaCommentForSite: t.string,
        currentLeadRegulatoryAgency: t.string,
        currentSiteActivity: t.string,
        pastSiteActivity: t.string,
        statusOfContaminationEvent: t.string,
        eventCriteriaSelection: t.string,
        criteriaCommentForContaminationEvent: t.string,
        commentsForContaminationEvent: t.string,
        isDUAExcluded: t.string,
        DUADetails: t.string,
        isTier2FALExcluded: t.string,
        FALAssessmentDetails: t.string,
        subsoilCriteriaApplies3m: t.string,
        isAgricultural: t.string,
        isCommercial: t.string,
        isIndustrial: t.string,
        isNatural: t.string,
        isResidential: t.string,
        isMoreSensetiveLandWithin30m: t.string,
        mostSensetiveLandUse: t.string,
        isCoarse: t.string,
        isFine: t.string,
        waterTableDepth: t.union([t.number, t.string]),
        thicknessOfNonAqueousPhaseLiquids: t.union([t.number, t.string, t.undefined]),
        directionOfGroundWaterFlow: t.string,
        allSubstancesRemediatedToCriteria: t.string,
        humanHealthConcernsOrExceedances: t.string,
        onSiteSoil: t.string,
        onSiteGroundWater: t.string,
        onSiteSurfaceWater: t.string,
        offSiteSoil: t.string,
        offSiteGroundWater: t.string,
        offSiteSurfaceWater: t.string,
        airSparging: t.string,
        alluBucket: t.string,
        bioPiles: t.string,
        bioSparging: t.string,
        bioventing: t.string,
        chemicalOxidation: t.string,
        digAndDump: t.string,
        dualPhaseExtraction: t.string,
        enhancedAerobicBioRemediation: t.string,
        inSituGroundwaterBioRemediation: t.string,
        landFarming: t.string,
        lowTempThermalDesorption: t.string,
        monitoredNaturalAttenuation: t.string,
        soilVaporExtraction: t.string,
        other: t.string,
        approvedRMPInPlace: t.string,
        DUA: t.string,
        FAL: t.string,
        managementLimit: t.string,
        ecologicalDirectSoilContact: t.string,
        humanDirectSoilContact: t.string,
        vapourInhalation: t.string,
        others: t.string,
        administrativeControl: t.string,
        exposureBarriers: t.string,
        thirdPartyAcceptanceApproved: t.string,
        monitoringReportDueDate: t.union([t.number, t.string, t.undefined]),
        additionalEventComments: t.string,
        additionalRiskManagementComments: t.string,
        closure: t.string,
        notedPotentialDeficiencies: t.string,
        timeForESASpecialist: t.union([t.number, t.string, t.undefined]),
        initials: t.string,
        approvedBySeniorReviewer: t.string,
        notes: t.string,
        dateSubmitted: t.string,
        dateOfCommentsProvidedAHEP: t.string,
        dateCompletedOrApproved: t.string,
        dateLogged: t.string,
        fileClassification: t.string
    })]);
export type SiteIO = t.TypeOf<typeof SiteTS>;

const SitesListTS = t.array(SiteTS);

export type SitesListIO = t.TypeOf<typeof SitesListTS>;

type Api = {
    fetchSites: () => Promise<SitesListIO>;
}

export default (): Api => ({
    fetchSites: async (): Promise<SitesListIO> => {
        const baseUrl = process.env.BASE_URL;
        const audience = ['public', 'reddeer'].includes(process.env.VUE_APP_AUDIENCE) ? process.env.VUE_APP_AUDIENCE : 'public';
        const response = await fetch(`${baseUrl}data/siteslist.${audience}.min.json`);
        if (!response.ok) {
            console.warn(`HTTP-Error: ${response.status}`);
            return [];
        }

        // console.log(response.json());
        const sitesListJson = await response.json();

        const parsedData = parse(SitesListTS, sitesListJson);
        return parsedData;
    }
});
